// RamsDocs template library — taxonomy + templates, DERIVED from the content-engine
// catalog via lib/catalog-index.json (emitted by `npm run export:index`, graph-free).
// This replaces the old hand-kept copy — single source of truth, no drift, and new
// categories/templates appear automatically. Bodies still come from the exported
// payloads (content/templates/*.json) on the template page; this file is metadata.
//
// REGENERATE: in content-engine run `npm run export:index` (writes this JSON), commit.

import catalogIndex from "./catalog-index.json";

// Category keys mirror content-engine catalog.ts. Kept as an explicit union (not
// widened to string) so the per-category content maps stay type-checked exhaustive.
export type CategoryKey =
  | "work-at-height"
  | "electrical"
  | "groundworks"
  | "lifting-plant"
  | "hot-works"
  | "coshh-substances"
  | "demolition-strip"
  | "trades-finishing"
  | "building-services"
  | "fit-out"
  | "site-general";

/** Review tier from the catalog index: high-risk tasks need competent-person review. */
export type ReviewTier = "human" | "ai";

export interface Category {
  key: CategoryKey;
  label: string;
  blurb: string;
}

export const CATEGORIES: Category[] = catalogIndex.categories.map((c) => ({
  key: c.key as CategoryKey,
  label: c.label,
  blurb: c.blurb,
}));

export interface TemplateSeed {
  id: string;
  category: CategoryKey;
  trade: string;
  task: string;
  title: string;
  targetKeyword: string;
  /** false = library-only (no standalone SEO page); see catalog.ts STRATEGY. */
  indexable: boolean;
  reviewTier: ReviewTier;
}

const CATALOG_TEMPLATES: TemplateSeed[] = catalogIndex.templates.map((t) => ({
  id: t.id,
  category: t.category as CategoryKey,
  trade: t.trade,
  task: t.task,
  title: t.title,
  targetKeyword: t.targetKeyword,
  indexable: t.indexable,
  reviewTier: t.reviewTier as ReviewTier,
}));

// Trade-task-backed templates: the content-engine models these in trades.ts (as
// reviewed trade-tasks with builder reports), not in catalog.ts, so they are not in
// the catalog index — but the site library still exposes them. Keep here until the
// catalog absorbs them. They have real reviewed bodies, so they stay page-indexable.
const TRADE_TASK_TEMPLATES: TemplateSeed[] = [
  { id: "elec-consumer-unit", category: "electrical", trade: "Electrical", task: "Consumer unit replacement", title: "Consumer Unit Replacement RAMS", targetKeyword: "consumer unit replacement risk assessment", indexable: true, reviewTier: "human" },
  { id: "hot-boiler-install", category: "hot-works", trade: "Plumbing & Heating", task: "Boiler installation", title: "Boiler Installation RAMS", targetKeyword: "boiler installation risk assessment", indexable: true, reviewTier: "human" },
];

export const TEMPLATES: TemplateSeed[] = [...CATALOG_TEMPLATES, ...TRADE_TASK_TEMPLATES];

export interface Trade {
  slug: string;
  name: string;
  headline: string;
  description: string;
  tasks: string[];
  /** false = no hero photo yet (/images/trade-<slug>.jpg missing) — render the
   *  plain navy hero instead of a broken image. Defaults to true. */
  heroImage?: boolean;
}

// Plumbing & Heating first — both current validation leads are plumbing/heating (handoff 7af32d99).
export const TRADES: Trade[] = [
  { slug: "plumbing-heating", name: "Plumbing & Heating", headline: "Plumbing & heating RAMS, site-specific in minutes", description: "Domestic and commercial plumbing, heating and pipework installation and maintenance.", tasks: ["Boiler installation", "Pipework & soldering", "Plant room works", "Bathroom installation"] },
  { slug: "electrical", name: "Electrical", headline: "Electrical method statements without the evening admin", description: "Electrical installation, testing and maintenance for domestic and commercial sites.", tasks: ["Isolation & lock-off", "Testing & inspection", "Containment", "Working at height"] },
  { slug: "scaffolding", name: "Scaffolding", headline: "Scaffolding RAMS built for principal-contractor review", description: "Erection, alteration and dismantling of access scaffolding and edge protection.", tasks: ["Erection sequence", "Loading bay", "TG20 compliance", "Edge protection"] },
  { slug: "groundworks", name: "Groundworks", headline: "Groundworks RAMS built around the dig", description: "Excavation, drainage, foundations and below-ground services.", tasks: ["Excavation", "Services / CAT scanning", "Shoring", "Plant & exclusion zones"] },
  { slug: "roofing", name: "Roofing", headline: "Roofing RAMS your PC can actually review", description: "Pitched and flat roofing, re-covers, repairs and associated work at height.", tasks: ["Working at height", "Fragile surfaces", "Edge protection", "MEWP use"] },
  { slug: "fencing", name: "Fencing", headline: "Fencing RAMS built around the dig and the boundary", description: "Boundary, security and highway fencing — installation, post-setting, palisade and removal.", tasks: ["Post setting & concreting", "Buried services / CAT scan", "Palisade & security fencing", "Roadside / highway fencing"] },
  { slug: "hvac", name: "HVAC & Air Conditioning", headline: "HVAC & air conditioning RAMS reviewers accept", description: "Air conditioning, refrigerant, ductwork and AHU installation, with F-gas and work-at-height controls.", tasks: ["Split-system installation", "Refrigerant pipework & brazing", "F-gas charging & recovery", "Rooftop plant lifting"] },
  { slug: "cleaning", name: "Cleaning", headline: "Cleaning RAMS, from builders clean to high-level", description: "Builders/sparkle clean, commercial and external cleaning, with COSHH, slips and work-at-height controls.", tasks: ["Builders / sparkle clean", "Commercial cleaning", "External window cleaning", "Jet washing"] },
  { slug: "landscaping-tree-surgery", name: "Landscaping & Tree Surgery", headline: "Tree surgery & landscaping RAMS built for the saw and the site", description: "Tree surgery, felling, chipping and soft/hard landscaping, with chainsaw, plant and public controls.", tasks: ["Tree surgery & pruning", "Tree felling", "Wood chipper & stump grinding", "Soft & hard landscaping"] },
  { slug: "rope-access", name: "Rope Access", headline: "IRATA rope-access RAMS with the rescue plan reviewers want", description: "Rope-access work positioning, inspection and cleaning on two-rope systems.", tasks: ["Work positioning (IRATA)", "Building inspection", "Facade cleaning"] },
  { slug: "steel-erection", name: "Steel Erection", headline: "Steel erection RAMS built around the lift and the leading edge", description: "Structural steel erection, safety netting and metal decking, with lifting and work-at-height controls.", tasks: ["Structural steel erection", "Safety netting", "Metal decking"] },
  { slug: "paving-surfacing", name: "Paving & Surfacing", headline: "Paving & surfacing RAMS for the dig, the plant and the road", description: "Block paving, tarmac/asphalt surfacing and kerbing, with hot-materials, plant and traffic controls.", tasks: ["Block paving & flags", "Tarmac & asphalt", "Kerbing & edgings"] },
  { slug: "security-systems", name: "Security Systems", headline: "Security-systems RAMS reviewers accept", description: "Intruder alarm, access control and roller-shutter installation, with work-at-height and electrical controls.", tasks: ["Intruder alarm", "Access control", "Roller shutters & barriers"] },
  { slug: "fire-doors", name: "Fire Doors", headline: "Fire-door RAMS built around the certification and the compliance regime", description: "Fire-door installation, inspection and fire dampers — passive fire protection under BS 8214 and the Fire Safety (England) Regulations 2022.", tasks: ["Fire door installation", "Fire door inspection", "Fire damper install & inspection"] },
  { slug: "asbestos", name: "Asbestos", headline: "Non-licensed asbestos RAMS that keep the licensed line explicit", description: "Asbestos risk assessment, sampling, non-licensed removal, encapsulation and disposal under CAR 2012 — with the licensed / NNLW / non-licensed distinction made clear.", tasks: ["Asbestos risk assessment", "Non-licensed removal", "Sampling", "Encapsulation & disposal"] },
  { slug: "rescue-plans", name: "Rescue Plans", headline: "Rescue plans that don't rely on calling 999", description: "Standalone rescue plans for confined space, MEWP and work at height — the part of a RAMS reviewers reject most.", tasks: ["Confined space rescue", "MEWP rescue", "Work at height rescue"] },
  { slug: "events", name: "Events", headline: "Event risk assessments for crowds, structures and power", description: "Event and temporary-structure risk assessment — crowds, stages and rigging, electrics and emergency planning.", tasks: ["Event risk assessment", "Temporary structures", "Crowd & emergency planning"] },
  { slug: "warehousing", name: "Warehousing", headline: "Warehouse risk assessments built around racking and FLTs", description: "Warehouse and storage operations — racking, forklift and pedestrian segregation, manual handling and loading.", tasks: ["Warehouse risk assessment", "Forklift operations", "Racking & manual handling"] },
  { slug: "facilities-management", name: "Facilities Management", headline: "FM & reactive-maintenance RAMS across occupied buildings", description: "Facilities management and reactive maintenance — mixed trades, occupied premises and the public.", tasks: ["FM & reactive maintenance", "Working in occupied premises", "Permit-controlled work"] },
];

export function categoryOf(key: CategoryKey): Category {
  return CATEGORIES.find((c) => c.key === key)!;
}

export function templatesByCategory(key: CategoryKey): TemplateSeed[] {
  return TEMPLATES.filter((t) => t.category === key);
}

/** Canonical keyword-friendly URL slug, derived from the title
 *  (e.g. "Scaffold Erection & Dismantling RAMS" -> scaffold-erection-dismantling-rams).
 *  Old internal-id URLs 301 to these in public/_worker.js. */
export function templateSlug(t: TemplateSeed): string {
  return t.title.toLowerCase().replace(/&/g, " ").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

/** Lookup by canonical slug or legacy internal id. */
export function getTemplate(idOrSlug: string): TemplateSeed | undefined {
  return TEMPLATES.find((t) => t.id === idOrSlug || templateSlug(t) === idOrSlug);
}

const CATEGORY_COVERAGE: Record<CategoryKey, string[]> = {
  "work-at-height": ["Work at height", "Access method", "Rescue / emergency"],
  electrical: ["Electrical isolation", "Competence", "Permit / site rules"],
  groundworks: ["Excavation", "Services", "Permit to dig"],
  "lifting-plant": ["Plant / lifting", "Exclusion zone", "Competence"],
  "hot-works": ["Hot works", "Fire watch", "Permit trigger"],
  "coshh-substances": ["COSHH", "SDS / substance", "Exposure controls"],
  "demolition-strip": ["Demolition", "Asbestos trigger", "Exclusion zone"],
  "trades-finishing": ["Trade-specific task", "Manual handling", "COSHH trigger"],
  "building-services": ["Service isolation", "Competence", "Hot works / permit"],
  "fit-out": ["Trade task", "Dust control", "Manual handling"],
  "site-general": ["Site arrangements", "Interfaces", "Briefing"],
};

const TEMPLATE_SEARCH_SYNONYMS: Record<string, string[]> = {
  "hot-boiler-install": ["gas boiler", "boiler swap", "heating", "flue", "gas safe", "soldering"],
  "elec-consumer-unit": ["fuse board", "distribution board", "db replacement", "consumer unit"],
  "ground-underground-services": ["buried services", "cat scan", "cable avoidance", "permit to dig"],
  "ground-excavation": ["digging", "foundations", "earthworks", "permit to dig"],
  "wah-mobile-tower": ["tower", "pasma", "mobile scaffold"],
  "wah-mewp": ["cherry picker", "boom lift", "scissor lift", "ipaf"],
  "lift-crane": ["mobile crane", "mobile", "crawler crane", "tower crane", "lorry loader", "hiab", "contract lift"],
  "bs-gas-heating": ["gas pipe", "pipework", "pipe", "gas main", "gas safe", "boiler", "heating"],
  "bs-plumbing": ["pipe", "pipework", "copper pipe", "waste pipe", "soil pipe"],
  // From PostHog template_search_zero_results: terms that map to a real template
  // but weren't in its haystack (entertainment->event, aircon->AC, removals plural).
  "event-rams": ["entertainment", "festival", "fun day", "event production", "live event"],
  "hvac-split-install": ["aircon", "air con", "ac unit", "split system", "air conditioner"],
  "demo-soft-strip": ["removals", "removal", "strip out"],
  "hot-cutting-grinding": ["disc cutter", "angle grinder", "abrasive wheel"],
  "coshh-silica": ["rcs", "dust", "cutting dust", "stone dust"],
  "coshh-cement": ["concrete", "mortar", "wet cement", "skin burn"],
  "demo-asbestos-awareness": ["acm", "asbestos", "non licensed", "refurbishment survey"],
  "site-occupied": ["occupied premises", "public", "live building", "residents"],
  "site-noise-havs": ["vibration", "havs", "noise", "hand arm vibration"],
};

/** Coverage chips shown on library cards and used for search/filtering.
 *  Keep this short: it is a findability cue, not a RAMS control library. */
export function coverageForTemplate(t: TemplateSeed): string[] {
  const out = new Set<string>(CATEGORY_COVERAGE[t.category] || []);
  const text = `${t.id} ${t.title} ${t.task} ${t.targetKeyword}`.toLowerCase();
  if (/hot|weld|cutting|grinding|torch|solder/.test(text)) out.add("Hot works");
  if (/coshh|silica|cement|solvent|paint|lead|dust|substance|chemical/.test(text)) out.add("COSHH");
  if (/height|roof|scaffold|tower|ladder|mewp|harness|edge/.test(text)) out.add("Work at height");
  if (/excavat|trench|underground|service|drainage|dig/.test(text)) out.add("Groundworks");
  if (/crane|lifting|telehandler|excavator|dumper|plant|manual handling/.test(text)) out.add("Plant / lifting");
  if (/confined/.test(text)) out.add("Confined space");
  if (/asbestos/.test(text)) out.add("Asbestos trigger");
  if (/permit|hot|dig|confined|crane|temporary works/.test(text)) out.add("Permit trigger");
  return Array.from(out).slice(0, 4);
}

export function templateSearchText(t: TemplateSeed): string {
  return [
    t.title,
    t.trade,
    t.task,
    t.targetKeyword,
    categoryOf(t.category).label,
    ...coverageForTemplate(t),
    ...(TEMPLATE_SEARCH_SYNONYMS[t.id] || []),
  ].join(" ").toLowerCase();
}
